import React, { useState, useEffect } from "react";
import { IconButton, Chip } from "@mui/material";
import ContentCutIcon from "@mui/icons-material/Close";

import { DroppedItem } from "./types"; // or wherever your DroppedItem interface is
import { CommodeIcon } from "../../components/icons/Commode";
import { KitchenBurnerIcon } from "../../components/icons/KitchenBurner";
import { Pillar } from "../../components/icons/Pillar";
import { EntranceIcon } from "../../components/icons/Entrance";
import { getColorHexCode } from "../../utils/helpers/getColorHexCode";

export type Corner = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

export interface SitePlanDroppedItemsOverlayProps {
  droppedItems: DroppedItem[];

  onMouseDown: (e: React.MouseEvent<HTMLDivElement>, itemId: string) => void;
  onTouchStart: (e: React.TouchEvent<HTMLDivElement>, itemId: string) => void;
  onContextMenu: (e: React.MouseEvent, itemId: string) => void;

  onResizeMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string,
    corner: Corner
  ) => void;
  onResizeTouchStart: (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string,
    corner: Corner
  ) => void;

  onRotateMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string
  ) => void;
  onRotateTouchStart: (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string
  ) => void;

  onCutItem: (itemId: string) => void;

  selectedItemId?: string;
  onSelectItem?: (itemId: string) => void;

  colors?: any[];
}

const cornerSize = 10;

const SitePlanDroppedItemsOverlay: React.FC<
  SitePlanDroppedItemsOverlayProps
> = ({
  droppedItems,
  onMouseDown,
  onTouchStart,
  onContextMenu,
  onResizeMouseDown,
  onResizeTouchStart,
  onRotateMouseDown,
  onRotateTouchStart,
  onCutItem,
  selectedItemId,
  onSelectItem,
  colors,
}) => {
  const [localSelectedItemId, setLocalSelectedItemId] = useState<string | null>(
    selectedItemId ?? null
  );

  useEffect(() => {
    if (selectedItemId !== undefined) {
      setLocalSelectedItemId(selectedItemId);
    }
  }, [selectedItemId]);

  const handleSelect = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    itemId: string
  ) => {
    e.stopPropagation();
    setLocalSelectedItemId(itemId);
    onSelectItem?.(itemId);
  };

  const rotateHandleStyle: React.CSSProperties = {
    position: "absolute",
    top: "-20px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "16px",
    height: "16px",
    backgroundColor: "#fff",
    border: "2px solid #4c9ffe",
    borderRadius: "50%",
    cursor: "grab",
    zIndex: 1000,
    touchAction: "none",
  };

  const cornerStyle = (cursor: React.CSSProperties["cursor"]) => ({
    position: "absolute" as const,
    width: `${cornerSize}px`,
    height: `${cornerSize}px`,
    backgroundColor: "#fff",
    border: "2px solid #4c9ffe",
    cursor,
  });

  return (
    <>
      {droppedItems.map((item) => {
        const { id, type, rotation = 0, width = 50, height = 50 } = item;
        const isSelected = localSelectedItemId === id;
        const isRoom = type === "rooms" || type === "Room"; // or however you differentiate

        let itemContent: React.ReactNode = null;
        if (isRoom) {
          itemContent = (
            <div
              style={{
                backgroundColor: "#444",
                color: "white",
                width: "100%",
                height: "100%",
                position: "relative",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              }}
            >
              <Chip
                label={item.displayName}
                sx={{
                  color: "white",
                  backgroundColor: "transparent",
                  fontWeight: "bold",
                  pointerEvents: "none",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </div>
          );
        } else {
          // Possibly any other item type
          if (type === "toiletSeat") {
            itemContent = (
              <CommodeIcon
                width={width}
                height={height}
                colour={getColorHexCode(item.color ?? "", colors ?? [])}
              />
            );
          } else if (type === "kitchenBurnerSlab") {
            itemContent = (
              <KitchenBurnerIcon
                width={width}
                height={height}
                colour={getColorHexCode(item.color ?? "", colors ?? [])}
              />
            );
          } else if (type === "housePillar") {
            itemContent = (
              <Pillar
                width={width}
                height={height}
                colour={getColorHexCode(item.color ?? "", colors ?? [])}
              />
            );
          } else if (type === "entrances") {
            itemContent = (
              <EntranceIcon
                width={width}
                height={height}
                colour={getColorHexCode(item.color ?? "", colors ?? [])}
              />
            );
          } else if (type === "wall") {
            itemContent = (
              <div
                style={{
                  backgroundColor: "#444",
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                }}
              />
            );
          } else {
            // fallback if unrecognized type
            itemContent = (
              <div
                style={{
                  backgroundColor: "#999",
                  width: "100%",
                  height: "100%",
                }}
              />
            );
          }
        }

        return (
          <div
            key={id}
            id={id}
            style={{
              position: "absolute",
              left: item.x,
              top: item.y,
              width,
              height,
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
              transformOrigin: "center center",
              userSelect: "none",
              cursor: "grab",
            }}
            onMouseDown={(e) => {
              handleSelect(e, id);
              onMouseDown(e, id);
            }}
            onTouchStart={(e) => {
              handleSelect(e, id);
              onTouchStart(e, id);
            }}
            onContextMenu={(e) => onContextMenu(e, id)}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {itemContent}
            </div>

            {isSelected && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "2px solid #4c9ffe",
                    boxSizing: "border-box",
                    pointerEvents: "none",
                  }}
                />

                <div
                  style={rotateHandleStyle}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onRotateMouseDown(e, id);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    onRotateTouchStart(e, id);
                  }}
                />

                <div
                  style={{
                    ...cornerStyle("nwse-resize"),
                    top: `-${cornerSize / 2}px`,
                    left: `-${cornerSize / 2}px`,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onResizeMouseDown(e, id, "topLeft");
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    onResizeTouchStart(e, id, "topLeft");
                  }}
                />
                <div
                  style={{
                    ...cornerStyle("nesw-resize"),
                    top: `-${cornerSize / 2}px`,
                    right: `-${cornerSize / 2}px`,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onResizeMouseDown(e, id, "topRight");
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    onResizeTouchStart(e, id, "topRight");
                  }}
                />
                <div
                  style={{
                    ...cornerStyle("nesw-resize"),
                    bottom: `-${cornerSize / 2}px`,
                    left: `-${cornerSize / 2}px`,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onResizeMouseDown(e, id, "bottomLeft");
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    onResizeTouchStart(e, id, "bottomLeft");
                  }}
                />
                <div
                  style={{
                    ...cornerStyle("nwse-resize"),
                    bottom: `-${cornerSize / 2}px`,
                    right: `-${cornerSize / 2}px`,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onResizeMouseDown(e, id, "bottomRight");
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    onResizeTouchStart(e, id, "bottomRight");
                  }}
                />

                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "-35px",
                    right: "-35px",
                    color: "#fff",
                    backgroundColor: "rgba(187,0,0,0.9)",
                    ":hover": { backgroundColor: "rgba(187,0,0,0.7)" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCutItem(id);
                  }}
                >
                  <ContentCutIcon fontSize="inherit" />
                </IconButton>
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

export default SitePlanDroppedItemsOverlay;
